import { useRef, useCallback } from 'react';
import LibraryActivityHorizontalCard from './LibraryActivityHorizontalCard';
import type { HorizontalCardActivity } from './LibraryActivityHorizontalCard';
import { cardWidth, skeletonBg } from './constants';

const DRAG_THRESHOLD = 5;

interface HorizontalActivityCarouselProps {
  activities: HorizontalCardActivity[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  isLoading?: boolean;
  skeletonCount?: number;
  /** Fixed pixel indent — use for accordion inset. Omit for page-level carousels (uses responsive padding). */
  startPadding?: number;
}

export default function HorizontalActivityCarousel({
  activities,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  isLoading,
  skeletonCount = 2,
  startPadding,
}: HorizontalActivityCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pointerDown = useRef(false);
  const captured = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartLeft = useRef(0);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    pointerDown.current = true;
    captured.current = false;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    scrollStartLeft.current = el.scrollLeft;
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!pointerDown.current || !scrollRef.current) return;
    const dx = e.clientX - dragStartX.current;

    if (!captured.current && Math.abs(dx) > DRAG_THRESHOLD) {
      captured.current = true;
      hasDragged.current = true;
      scrollRef.current.setPointerCapture(e.pointerId);
      scrollRef.current.style.cursor = 'grabbing';
      scrollRef.current.style.userSelect = 'none';
    }

    if (captured.current) {
      scrollRef.current.scrollLeft = scrollStartLeft.current - dx;
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!pointerDown.current) return;
    pointerDown.current = false;
    const el = scrollRef.current;
    if (el && captured.current) {
      el.releasePointerCapture(e.pointerId);
      el.style.cursor = 'grab';
      el.style.userSelect = '';
    }
    captured.current = false;
  }, []);

  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (hasDragged.current) {
      e.stopPropagation();
      e.preventDefault();
      hasDragged.current = false;
    }
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasNextPage || isFetchingNextPage || !onLoadMore) return;
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 100) {
      onLoadMore();
    }
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);

  return (
    <div
      ref={scrollRef}
      className="overflow-x-auto"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
        cursor: 'grab',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClickCapture={handleClickCapture}
      onScroll={handleScroll}
    >
      <div
        className={`flex gap-[16px] pb-4 ${startPadding === undefined ? 'px-6 sm:px-8 md:px-10 lg:px-12' : ''}`}
        style={startPadding !== undefined ? { paddingLeft: startPadding, paddingRight: 24 } : undefined}
      >
        {isLoading
          ? Array.from({ length: skeletonCount }).map((_, i) => (
              <div
                key={i}
                className={`flex-shrink-0 ${cardWidth} h-[494px] rounded-[24px] ${skeletonBg} animate-pulse`}
              />
            ))
          : activities.map((activity) => (
              <LibraryActivityHorizontalCard key={activity.id} activity={activity} />
            ))}
        {isFetchingNextPage && (
          <div className={`flex-shrink-0 ${cardWidth} h-[494px] rounded-[24px] ${skeletonBg} animate-pulse`} />
        )}
      </div>
    </div>
  );
}
